import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import WordPages from '../components/WordLayout';
import { signSummary, signSendCode, signVerifyCode, signDocs, signComplete } from '../data/legalApi';

// The guest signing ceremony (/sign/:token) — the DocuSign-style flow:
//
//   1. Gate: envelope summary + one-time code to the signer's email on file
//      (the backend never reveals the address — only a masked hint).
//   2. Consent: the ESIGN/UETA disclosure, recorded with the signature.
//   3. Review & sign: every document rendered in the Word print layout
//      (read-only, exactly what the executed PDF will look like), with a
//      guided walk through the signer's clause-anchored tabs — Next scrolls
//      to and outlines the section, the action bar applies the adopted
//      signature/initials.
//   4. Done: evidence hashes shown; executed copies arrive by email once
//      every signer has finished.
//
// No Firebase login, no Firestore access — everything goes through the
// token-authenticated ceremony API (backend/api/signApi.js).

const SCRIPT_STACK = "'Great Vibes', 'Snell Roundhand', cursive";

const initialsOf = (name) => String(name || '')
  .split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase()).join('');

export default function SignCeremony() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | gate | consent | sign | done | blocked
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [codeSentTo, setCodeSentTo] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [consentText, setConsentText] = useState('');
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [payload, setPayload] = useState(null); // signDocs response
  const [adoptedName, setAdoptedName] = useState('');
  const [adoptedInitials, setAdoptedInitials] = useState('');
  const [values, setValues] = useState({}); // fieldId -> value (my completed tabs)
  const [activeIdx, setActiveIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // The executed-signature script font, loaded only on this public page.
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  useEffect(() => {
    let alive = true;
    signSummary(token)
      .then((s) => {
        if (!alive) return;
        setSummary(s);
        setAdoptedName(s.signer?.name || '');
        setAdoptedInitials(initialsOf(s.signer?.name));
        setPhase('gate');
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.message);
        setPhase('blocked');
      });
    return () => { alive = false; };
  }, [token]);

  // My tabs in reading order: documents in envelope order, sections in
  // document order (date tabs ride their signature and are auto-filled).
  const myFields = useMemo(() => {
    if (!payload) return [];
    const clauseIdx = new Map();
    payload.docs.forEach((d, di) => d.clauses.forEach((c, ci) => clauseIdx.set(`${d.docId}:${c.id}`, di * 100000 + ci)));
    return (payload.fields || [])
      .filter((f) => f.signerKey === payload.signerKey && f.type !== 'date')
      .sort((a, b) => (clauseIdx.get(`${a.docId}:${a.clauseId}`) ?? 0) - (clauseIdx.get(`${b.docId}:${b.clauseId}`) ?? 0));
  }, [payload]);

  const doneCount = myFields.filter((f) => values[f.id]).length;
  const nextIncomplete = myFields.findIndex((f) => !values[f.id]);
  const active = activeIdx >= 0 ? myFields[activeIdx] : null;

  const sendCode = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await signSendCode(token);
      setCodeSentTo(r.emailMasked);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await signVerifyCode(token, code.trim());
      setSession(r.session);
      setConsentText(r.consentText);
      setPhase('consent');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openDocs = async () => {
    setBusy(true);
    setError('');
    try {
      const p = await signDocs(token, session);
      setPayload(p);
      // Pre-fill values already signed (revisit after completion).
      if (p.alreadySigned || p.status === 'completed' || p.status === 'completing') {
        setPhase('done');
        setResult({ already: true });
      } else {
        setPhase('sign');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const goTo = (idx) => {
    const f = myFields[idx];
    if (!f) return;
    setActiveIdx(idx);
    const di = payload.docs.findIndex((d) => d.docId === f.docId);
    document.getElementById(`d${di}-clause-${f.clauseId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const applyActive = () => {
    if (!active) return;
    const value = active.type === 'signature' ? adoptedName.trim() : adoptedInitials.trim();
    if (!value) return;
    const next = { ...values, [active.id]: value };
    setValues(next);
    const idx = myFields.findIndex((f, i) => i !== activeIdx && !next[f.id] && i > activeIdx);
    const fallback = myFields.findIndex((f) => !next[f.id]);
    const target = idx >= 0 ? idx : fallback;
    if (target >= 0) goTo(target);
    else setActiveIdx(-1);
  };

  const finish = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await signComplete(token, { session, values, consentAgreed: true });
      setResult(r);
      setPhase('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- screens ---------------------------------------------------------------

  const shell = (inner, wide) => (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      <div style={{ maxWidth: wide ? 1000 : 520, margin: '0 auto', padding: 'var(--sp-5)' }}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 'var(--sp-5)' }}>
          fast.legal <span className="u-muted" style={{ fontWeight: 400, fontSize: 13 }}>· electronic signature</span>
        </div>
        {inner}
      </div>
    </div>
  );

  if (phase === 'loading') return shell(<div className="cs-empty">Loading…</div>);

  if (phase === 'blocked') {
    return shell(
      <div className="cs-card" style={{ padding: 'var(--sp-5)' }}>
        <h2 style={{ marginTop: 0 }}>This signing link isn't available</h2>
        <p className="u-small u-muted">{error}</p>
        <p className="u-small u-muted">If you were expecting to sign, ask the sender for a fresh link.</p>
      </div>,
    );
  }

  if (phase === 'gate') {
    return shell(
      <div className="cs-card" style={{ padding: 'var(--sp-5)' }}>
        <h2 style={{ marginTop: 0 }}>{summary.title}</h2>
        <p className="u-small u-muted" style={{ marginTop: 0 }}>
          {summary.sender} has requested your signature
          {summary.docTitles?.length ? ` on ${summary.docTitles.length} document${summary.docTitles.length === 1 ? '' : 's'}` : ''}.
        </p>
        {summary.message && <p className="u-small" style={{ whiteSpace: 'pre-wrap' }}>{summary.message}</p>}
        <ul className="u-small u-muted" style={{ paddingLeft: 20 }}>
          {(summary.docTitles || []).map((t, i) => <li key={i}>{t}</li>)}
        </ul>
        <div style={{ borderTop: '1px solid var(--border)', margin: 'var(--sp-4) 0', paddingTop: 'var(--sp-4)' }}>
          <p className="u-small" style={{ marginTop: 0 }}>
            Signing as <strong>{summary.signer.name}</strong>. To verify it's you, we'll email a
            one-time code to <strong>{summary.signer.email}</strong>.
          </p>
          {!codeSentTo ? (
            <button className="cs-btn cs-btn-primary" disabled={busy} onClick={sendCode}>
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          ) : (
            <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
              <p className="u-xs u-muted" style={{ margin: 0 }}>Code sent to {codeSentTo}.</p>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                <input
                  className="cs-input"
                  style={{ width: 160, fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="······"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) verify(); }}
                />
                <button className="cs-btn cs-btn-primary" disabled={busy || code.length !== 6} onClick={verify}>
                  {busy ? 'Checking…' : 'Verify'}
                </button>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={sendCode}>Resend</button>
              </div>
            </div>
          )}
          {error && <p className="u-small" style={{ color: 'var(--danger)' }}>{error}</p>}
        </div>
      </div>,
    );
  }

  if (phase === 'consent') {
    return shell(
      <div className="cs-card" style={{ padding: 'var(--sp-5)' }}>
        <h2 style={{ marginTop: 0 }}>Electronic records &amp; signatures</h2>
        <p className="u-small" style={{ whiteSpace: 'pre-wrap' }}>{consentText}</p>
        <label className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'flex-start', cursor: 'pointer', margin: 'var(--sp-4) 0' }}>
          <input type="checkbox" checked={consentAgreed} onChange={(e) => setConsentAgreed(e.target.checked)} style={{ marginTop: 3 }} />
          <span className="u-small">I agree to use electronic records and signatures.</span>
        </label>
        <button className="cs-btn cs-btn-primary" disabled={!consentAgreed || busy} onClick={openDocs}>
          {busy ? 'Loading documents…' : 'Review documents'}
        </button>
        {error && <p className="u-small" style={{ color: 'var(--danger)' }}>{error}</p>}
      </div>,
    );
  }

  if (phase === 'done') {
    return shell(
      <div className="cs-card" style={{ padding: 'var(--sp-5)' }}>
        <h2 style={{ marginTop: 0 }}>{result?.already ? 'Already signed' : 'Signed ✓'}</h2>
        {result?.already ? (
          <p className="u-small u-muted">You've completed your part of "{summary?.title}".</p>
        ) : (
          <>
            <p className="u-small">
              Your signature on <strong>{summary?.title}</strong> is recorded and sealed.
            </p>
            {result?.sigHash && (
              <p className="u-xs u-muted" style={{ fontFamily: 'monospace' }}>
                evidence sha256 {result.sigHash.slice(0, 24)}…<br />
                chain {result.chainHash.slice(0, 24)}…
              </p>
            )}
          </>
        )}
        <p className="u-small u-muted">
          {result?.allSigned
            ? 'All parties have signed — executed copies and the certificate of completion are on their way to your email.'
            : 'Once every signer has finished, the executed copies and certificate of completion arrive by email.'}
        </p>
      </div>,
    );
  }

  // ---- phase 'sign' ----------------------------------------------------------
  const waiting = payload && !payload.yourTurn;
  return shell(
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)', paddingBottom: 120 }}>
      <div className="cs-card" style={{ padding: 'var(--sp-4)' }}>
        <div className="u-split">
          <div>
            <strong>{payload.title}</strong>
            <div className="u-xs u-muted">
              Signing as {summary.signer.name} · {payload.docs.length} document{payload.docs.length === 1 ? '' : 's'} ·{' '}
              {(payload.signers || []).map((s) => `${s.name}${s.status === 'signed' ? ' ✓' : ''}`).join(' → ')}
            </div>
          </div>
          {!waiting && (
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => goTo(nextIncomplete >= 0 ? nextIncomplete : 0)}>
              {doneCount === 0 ? 'Start' : nextIncomplete >= 0 ? 'Next tab' : 'Review'}
            </button>
          )}
        </div>
        {waiting && (
          <p className="u-small u-muted" style={{ margin: 'var(--sp-3) 0 0' }}>
            It's not your turn yet — an earlier signer hasn't finished. You can review the documents;
            signing unlocks when it's your turn.
          </p>
        )}
      </div>

      {payload.docs.map((d, di) => (
        <div key={d.docId}>
          <div className="u-small u-muted" style={{ margin: '0 0 var(--sp-2)', textAlign: 'center' }}>
            Document {di + 1} of {payload.docs.length} · v{d.versionNumber}
            {d.chainHash ? ` · sealed ${d.chainHash.slice(0, 12)}…` : ''}
          </div>
          <WordPages
            title={d.title}
            clauses={d.clauses}
            mode="clean"
            structured={false}
            versionKey={`${d.docId}:${d.versionNumber}`}
            totalKnown
            targetId={active && active.docId === d.docId ? active.clauseId : null}
            anchorPrefix={`d${di}-`}
          />
        </div>
      ))}

      {/* Sticky action bar — the guided signing walk. */}
      {!waiting && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
          background: 'var(--bg-raised, var(--bg))', borderTop: '1px solid var(--border)',
          padding: 'var(--sp-3) var(--sp-4)',
        }}
        >
          <div style={{ maxWidth: 1000, margin: '0 auto' }} className="u-split">
            <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center' }}>
              <span className="u-small u-muted">{doneCount} of {myFields.length} tabs done</span>
              {active && (
                <>
                  <span className="u-small">
                    {active.type === 'signature' ? 'Sign' : 'Initial'} here
                    {(() => {
                      const doc = payload.docs.find((x) => x.docId === active.docId);
                      const cl = doc?.clauses.find((c) => c.id === active.clauseId);
                      return cl ? ` — ${cl.heading || 'section'}` : '';
                    })()}
                  </span>
                  <span style={{ fontFamily: SCRIPT_STACK, fontSize: 22, color: 'var(--brand)' }}>
                    {active.type === 'signature' ? adoptedName : adoptedInitials}
                  </span>
                  {active.type === 'signature' ? (
                    <input className="cs-input" style={{ width: 180 }} value={adoptedName} onChange={(e) => { setAdoptedName(e.target.value); setAdoptedInitials(initialsOf(e.target.value)); }} aria-label="Adopted signature name" />
                  ) : (
                    <input className="cs-input" style={{ width: 70 }} value={adoptedInitials} maxLength={6} onChange={(e) => setAdoptedInitials(e.target.value)} aria-label="Adopted initials" />
                  )}
                  <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={applyActive} disabled={!(active.type === 'signature' ? adoptedName.trim() : adoptedInitials.trim())}>
                    {active.type === 'signature' ? 'Adopt & sign' : 'Initial'}
                  </button>
                </>
              )}
              {!active && nextIncomplete >= 0 && (
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => goTo(nextIncomplete)}>Go to next tab</button>
              )}
            </div>
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
              {error && <span className="u-xs" style={{ color: 'var(--danger)' }}>{error}</span>}
              <button className="cs-btn cs-btn-primary" disabled={busy || doneCount < myFields.length || !myFields.length} onClick={finish}>
                {busy ? 'Sealing…' : 'Finish & sign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    true,
  );
}
